import { useState } from 'react';
import { Button, Chip, Dialog, Select, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../../components/common';
import { useBuddyWrite } from '../../api/client';
import type { DbServer, DbServerInput, DbServerTestResult } from '../../api/setup';
import { SecretField, messageOf, useQueryParam, useQueryPatch, useSetup } from './shared';

const SLUG = /^[a-z0-9][a-z0-9_-]{0,63}$/;

type Form = DbServerInput & { password: string };

const empty: Form = {
  id: '',
  name: '',
  cluster: '',
  kind: 'postgres',
  host: '',
  port: 5432,
  username: '',
  password: '',
  sslMode: 'prefer',
  note: '',
};

const toForm = (s: DbServer): Form => ({
  id: s.id,
  name: s.name,
  cluster: s.cluster,
  kind: s.kind,
  host: s.host,
  port: s.port,
  username: s.username,
  password: '',
  sslMode: s.sslMode,
  note: s.note ?? '',
});

/** The body: the password only when typed (absent keeps the stored one). */
const serverBody = ({ password, ...rest }: Form): DbServerInput => ({
  ...rest,
  id: rest.id.trim(),
  host: rest.host.trim(),
  ...(password ? { password } : {}),
});

function ServerEditor({
  initial,
  stored,
  isNew,
  canWrite,
  onSave,
  onClose,
}: {
  initial: Form;
  stored: DbServer | null;
  isNew: boolean;
  canWrite: boolean;
  onSave: (body: DbServerInput) => Promise<void>;
  onClose: () => void;
}) {
  const [v, setV] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ro = !canWrite;
  const set = <K extends keyof Form>(k: K, value: Form[K]) => setV((cur) => ({ ...cur, [k]: value }));
  const idError = isNew && v.id && !SLUG.test(v.id) ? 'lowercase letters, digits, - and _' : undefined;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(serverBody(v));
      onClose();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={isNew ? 'New database server' : `Database server ${v.name || v.id}`}
      maxWidth={700}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {ro ? 'Close' : 'Cancel'}
          </Button>
          {!ro && (
            <Button
              onClick={() => void save()}
              loading={busy}
              disabled={!v.id.trim() || !v.host.trim() || !!idError}
            >
              Save
            </Button>
          )}
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="Id"
          value={v.id}
          onChange={(e) => set('id', e.target.value)}
          disabled={!isNew || ro}
          error={idError}
          hint={isNew ? 'lowercase slug, e.g. alloydb-prod; cannot be changed later' : 'cannot be changed'}
          required
        />
        <TextField label="Name" value={v.name} onChange={(e) => set('name', e.target.value)} disabled={ro} />
        <TextField
          label="Cluster"
          value={v.cluster}
          onChange={(e) => set('cluster', e.target.value)}
          hint="grouping label, e.g. logic-cluster-prod"
          disabled={ro}
        />
        <Select
          label="Kind"
          value={v.kind}
          onChange={(kind) => set('kind', kind)}
          options={[
            { value: 'postgres', label: 'Postgres' },
            { value: 'alloydb', label: 'AlloyDB' },
          ]}
          disabled={ro}
        />
        <TextField
          label="Host"
          className="nyk-mono"
          value={v.host}
          onChange={(e) => set('host', e.target.value)}
          hint="private address reachable from buddy3's API pods"
          disabled={ro}
          required
        />
        <TextField
          label="Port"
          type="number"
          inputMode="numeric"
          value={String(v.port)}
          onChange={(e) => set('port', Number(e.target.value) || 5432)}
          disabled={ro}
        />
        <TextField
          label="User"
          value={v.username}
          onChange={(e) => set('username', e.target.value)}
          disabled={ro}
        />
        <SecretField
          label="Password"
          value={v.password}
          stored={stored?.hasPassword}
          isNew={isNew}
          onChange={(x) => set('password', x)}
          disabled={ro}
        />
        <Select
          label="SSL"
          value={v.sslMode}
          onChange={(sslMode) => set('sslMode', sslMode)}
          options={['prefer', 'require', 'disable'].map((x) => ({ value: x, label: x }))}
          disabled={ro}
        />
        <TextField
          label="Note"
          fieldClassName="nyk-span2"
          value={v.note ?? ''}
          onChange={(e) => set('note', e.target.value)}
          disabled={ro}
        />
      </div>
    </Dialog>
  );
}

function TestCell({ result }: { result?: DbServerTestResult | 'busy' }) {
  if (!result) return <span className="nyk-ui-hint">—</span>;
  if (result === 'busy') return <span className="nyk-ui-hint">testing…</span>;
  return (
    <span data-test-result={result.ok ? 'ok' : 'fail'}>
      <Chip size="sm" className={result.ok ? 'nyk-tone-green' : 'nyk-tone-red'}>
        {result.ok ? `ok${result.ms != null ? `, ${result.ms} ms` : ''}` : 'failed'}
      </Chip>
      {result.ok && result.serverVersion && <div className="nyk-ui-hint">{result.serverVersion}</div>}
      {!result.ok && result.error && <div className="nyk-text-red">{result.error}</div>}
    </span>
  );
}

/**
 * The Postgres/AlloyDB servers buddy3 provisions NYCE.LOGIC objects into (questions always go through the workserver).
 * buddy3's own list, not the hub registry's: a tenant points at one of these plus its database name.
 */
export function BuddyDbServersPage() {
  const { api } = useSetup();
  const canWrite = useBuddyWrite();
  const servers = useAsync(() => api.dbServers.list(), [api]);
  const [edit] = useQueryParam('edit');
  const patch = useQueryPatch();
  const [tested, setTested] = useState<Record<string, DbServerTestResult | 'busy'>>({});
  const toast = useNotice();
  const list = servers.data ?? [];
  const isNew = edit === 'new' && canWrite;
  const stored = isNew ? null : (list.find((s) => s.id === edit) ?? null);
  const initial = isNew ? empty : stored ? toForm(stored) : null;

  const save = async (body: DbServerInput) => {
    if (isNew) await api.dbServers.create(body);
    else await api.dbServers.update(body.id, body);
    toast.success(`Saved ${body.name || body.id}`);
    await servers.reload();
  };
  const remove = async (s: DbServer) => {
    try {
      await api.dbServers.remove(s.id);
      toast.success(`Deleted ${s.name || s.id}`);
      await servers.reload();
    } catch (e) {
      toast.error(`Could not delete ${s.name || s.id}: ${messageOf(e)}`);
    }
  };
  const test = async (s: DbServer) => {
    setTested((t) => ({ ...t, [s.id]: 'busy' }));
    try {
      const r = await api.dbServers.test(s.id);
      setTested((t) => ({ ...t, [s.id]: r }));
    } catch (e) {
      setTested((t) => ({ ...t, [s.id]: { ok: false, error: messageOf(e) } }));
    }
  };
  const clusters = [...new Set(list.map((s) => s.cluster || 'Ungrouped'))].sort();

  return (
    <Page
      title="Database servers"
      description="Postgres and AlloyDB servers nyce.buddy reaches directly, only to provision NYCE.LOGIC objects; questions always go through the workserver."
      actions={canWrite && <Button onClick={() => patch({ edit: 'new' })}>New server</Button>}
    >
      <ErrorBox error={servers.error} />
      {toast.element}
      <Loading loading={servers.loading && !servers.data} />
      {servers.data && !list.length && (
        <EmptyState
          card
          title="No database servers yet."
          hint="Add a cluster's Postgres or AlloyDB instance, then point a tenant at it and its database to provision from here."
        />
      )}
      {clusters.map((cluster) => (
        <section className="nyk-card" key={cluster} data-cluster={cluster}>
          <div className="nyk-card-head">
            <h2>{cluster}</h2>
          </div>
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="db-servers-table">
              <thead>
                <tr>
                  <th>Server</th>
                  <th>Kind</th>
                  <th>Address</th>
                  <th>User</th>
                  <th className="nyk-ui-num">Systems</th>
                  <th>Connection</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list
                  .filter((s) => (s.cluster || 'Ungrouped') === cluster)
                  .map((s) => (
                    <tr key={s.id} data-db-server={s.id}>
                      <td className="nyk-card-title">
                        <b>{s.name || s.id}</b>
                        <div className="nyk-ui-hint">{s.id}</div>
                        {s.secretError && <div className="nyk-text-red">{s.secretError}</div>}
                      </td>
                      <td data-label="Kind">
                        <Chip size="sm">{s.kind}</Chip>
                      </td>
                      <td data-label="Address" className="nyk-url">
                        {s.host}:{s.port}
                        <div className="nyk-ui-hint">ssl {s.sslMode}</div>
                      </td>
                      <td data-label="User">
                        {s.username}
                        <div className="nyk-ui-hint" data-password={s.id}>
                          password {s.hasPassword ? 'stored' : 'not set'}
                        </div>
                      </td>
                      <td className="nyk-ui-num" data-label="Systems">
                        {s.systemCount ?? 0}
                      </td>
                      <td data-label="Connection">
                        <TestCell result={tested[s.id]} />
                      </td>
                      <td className="nyk-row-actions">
                        {canWrite && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void test(s)}
                            disabled={tested[s.id] === 'busy'}
                          >
                            Test
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => patch({ edit: s.id })}>
                          {canWrite ? 'Edit' : 'View'}
                        </Button>
                        {canWrite && (
                          <ConfirmButton
                            size="sm"
                            variant="danger"
                            message={`Delete server ${s.name || s.id}?`}
                            onConfirm={() => void remove(s)}
                          >
                            Delete
                          </ConfirmButton>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </TableScroller>
        </section>
      ))}
      {initial && (
        <ServerEditor
          key={edit}
          initial={initial}
          stored={stored}
          isNew={isNew}
          canWrite={canWrite}
          onSave={save}
          onClose={() => patch({ edit: null })}
        />
      )}
    </Page>
  );
}
